import type { Custodian } from 'models/custodian';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import type { MapStateToPropsFactory } from 'react-redux';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import {
  getAmountUnit,
  getCobDate,
  getRelevantPriceDependingOnTradeWay,
  getSalesMarginPlusUpfront,
  isOtcBcsProductKey,
} from 'state/secondaryMarket/cpOrder/cpOrder.utils';
import { getMaxQuantity } from 'state/secondaryMarket/cpOrder/cpOrderForm/cpOrderForm.selectors';
import {
  secondaryMarketCpOrderCashAmountChangedThunk,
  secondaryMarketCpOrderCashAmountResetThunk,
  secondaryMarketCpOrderClientSelectedThunk,
  secondaryMarketCpOrderClientTypeSelectedThunk,
  secondaryMarketCpOrderCustodianSelectedThunk,
  secondaryMarketCpOrderFormTradeDateTypeChangedThunk,
  secondaryMarketCpOrderFormValuationDateChangedThunk,
  secondaryMarketCpOrderQuantityChangedThunk,
  secondaryMarketCpOrderQuantityResetThunk,
  secondaryMarketCpOrderSalesMarginChangedThunk,
  secondaryMarketCpOrderSelectSalesDeskThunk,
  secondaryMarketCpOrderSelectSalesIdThunk,
  secondaryMarketCpOrderSettlementDateChangedThunk,
  secondaryMarketCpOrderSettlementTypeChangedThunk,
  secondaryMarketCpOrderTradeSizeInputModeChangedThunk,
  secondaryMarketCpOrderTradeWayChangedThunk,
  secondaryMarketCpOrderTradingPriceChangedThunk,
} from 'state/secondaryMarket/cpOrder/cpOrderForm/cpOrderForm.thunks';
import type { TradeDateType } from 'state/secondaryMarket/cpOrder/cpOrderForm/input/cpOrderInputForm.model';
import { secondaryMarketCpFormLinkedToClientRequested } from 'state/secondaryMarket/cpOrder/referential/client/client.actions';
import type { CpOrderSettlement, SalesDesk } from 'state/secondaryMarket/cpOrder/referential/client/client.model';
import {
  getCpOrderReferentialLotSize,
  getCpOrderReferentialMinQuantity,
  getCpOrderReferentialRemaining,
} from 'state/secondaryMarket/cpOrder/referential/product/product.selector';
import { getAllowedTradeWays } from 'state/secondaryMarket/cpOrder/referential/product/product.selector.bcs';
import type { SecondaryPriceType, SecondaryQuotationType } from 'state/secondaryMarket/productSearch';
import { allTradeWays, type TradeWay } from 'state/secondaryMarket/secondaryMarket.model';
import type { ClientType } from 'state/secondaryMarket/shared/form.models';
import type { OtcClient, Sales } from 'state/secondaryMarket/shared/referential/referential.model';
import type { TradeSizeInputMode } from 'state/secondaryMarket/shared/tradeInformation.models';
import { toSettlementTypeKey } from 'state/secondaryMarket/shared/utils';
import { getAvailableDesks, getAvailableSalesWithSelecteDesk, toBcsProductKey } from 'state/secondaryMarket/utils';
import { fromSecondaryMarket, fromSecondaryQuotesWorkspace } from 'state/selectors';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import type { WorkspaceType } from 'state/workspaces/model';
import { getTabIsFrozen } from 'state/workspaces/tabs/tabs.selector';
import type { LoadingStatus } from 'utils/status';

type OwnProps = Readonly<{
  tabId: string;
  workspaceType: WorkspaceType;
}>;

type StateProps = Readonly<{
  isinCode?: string;
  denomination?: number;
  fileCode?: string;
  ric?: string;
  maturityDate?: string;
  productClients?: ISalesCounterpart[];
  quotationType?: SecondaryQuotationType;
  priceType?: SecondaryPriceType;
  currency?: string;
  clientType: ClientType;
  isClientVisible: boolean;
  traderGroup?: string;
  isSalesVisible: boolean;
  portfolio?: string;
  isOtcProduct: boolean;
  traderMessage?: string;
  saleMessage?: string;
  quantity: number | undefined;
  cashAmount: number | undefined;
  tradeWay: TradeWay;
  needCustodian: boolean;
  selectedCustodian: Custodian | undefined;
  disabledTradeWays: readonly TradeWay[];
  salesMarginPlusUpfront: number | undefined;
  upfront: number | undefined;
  salesMarginAdjusted: number | undefined;
  tradingPriceAdjusted: number | undefined;
  amountUnit: string;
  cutOffTime: string | undefined;
  cutOffZoneId: string | undefined;
  nextCutOff: Date | undefined;
  settlementDate: string | undefined;
  firstSettlementDate: string | undefined;
  settlementHolidays: ReadonlyArray<string> | undefined;
  valuationDate: string | undefined;
  tradeDateType: TradeDateType;
  availableValuationDates: ReadonlyArray<string> | undefined;
  availableSettlementTypes: ReadonlyArray<CpOrderSettlement> | undefined;
  settlementType: string | undefined;
  cobDate: string | undefined;
  isLoadingSettlement: boolean;
  clientLoadingStatus: LoadingStatus;
  client?: ISalesCounterpart;
  clientEliotCode?: string;
  productOtcClients?: ReadonlyArray<OtcClient>;
  salesId?: string;
  availableSales: Sales[];
  selectedSalesDesk?: string;
  availableDesks: readonly SalesDesk[];
  isFrozen: boolean;
  tradeSizeInputMode: TradeSizeInputMode;
  minQuantity?: number;
  maxQuantity?: number;
  lot?: number;
  remaining?: number;
}>;

type DispatchProps = Readonly<{
  onClientTypeChanged: (value: ClientType) => void;
  onQuantityChanged: (value: number | undefined) => void;
  onQuantityReset: () => void;
  onCashAmountReset: () => void;
  onCashAmountChanged: (value: number | undefined) => void;
  onTradeWayChanged: (value: TradeWay) => void;
  onCustodianChanged: (value: Custodian | undefined | null) => void;
  onTradingPriceChanged: (value: number | undefined) => void;
  onSalesMarginChanged: (value: number | undefined) => void;
  onSettlementDateChanged: (value: Nullable<string>) => void;
  onValuationDateChanged: (value: Nullable<string>) => void;
  onSettlementTypeChanged: (value: string | undefined) => void;
  onTradeDateTypeChanged: (value: TradeDateType) => void;
  onClientChanged: (client: ISalesCounterpart) => void;
  onClientClear: () => void;
  onRetryLoadingClient: () => void;
  onSalesChanged: (salesId: string | undefined) => void;
  onSalesDeskChanged: (salesId: string | undefined) => void;
  onTradeSizeInputModeChanged: () => void;
}>;

export type CpOrderFormContentProps = OwnProps & StateProps & DispatchProps;

const mapStateToProps: MapStateToPropsFactory<StateProps, OwnProps, AppState> = () => {
  const getDisabledTradeways = createSelector([getAllowedTradeWays], allowedTradeways =>
    allTradeWays.filter(way => !allowedTradeways.includes(way)),
  );
  const getAvailableSales = createSelector(
    [
      (appState: AppState, tabId: string) =>
        fromSecondaryMarket.cpOrder.referential.client.getAllSales(appState, tabId),
      (appState: AppState, tabId: string) => fromSecondaryMarket.cpOrder.form.getFormSalesDesk(appState, tabId),
    ],
    (allSales, selectedSalesDesk) => getAvailableSalesWithSelecteDesk(allSales, selectedSalesDesk),
  );

  const getDesks = createSelector(
    [
      (appState: AppState, tabId: string) =>
        fromSecondaryMarket.cpOrder.referential.client.getAllSales(appState, tabId),
    ],
    allSales => getAvailableDesks(allSales),
  );

  return (state, { tabId }) => {
    const bcsKey = fromSecondaryQuotesWorkspace.getProductKey(state, tabId);
    const isinCode = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductIsin(state, tabId);
    const ric = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductRic(state, tabId);
    const fileCode = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductFileCode(state, tabId);
    const maturityDate = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductMaturityDate(
      state,
      tabId,
    )!;
    const quotationType = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductQuotationType(
      state,
      tabId,
    )!;
    const priceType = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductPriceType(state, tabId)!;
    const productClients = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductClients(state, tabId)!;
    const traderGroup = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductTraderGroup(state, tabId)!;
    const portfolio = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductPortfolio(state, tabId)!;
    const denomination = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductDenomination(
      state,
      tabId,
    )!;
    const isOtcProduct = isOtcBcsProductKey(toBcsProductKey(bcsKey));
    const traderMessage = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductTraderMessage(
      state,
      tabId,
    );
    const saleMessage = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductSalesMessage(state, tabId);
    const quantity = fromSecondaryMarket.cpOrder.form.getFormQuantity(state, tabId);
    const cashAmount = fromSecondaryMarket.cpOrder.form.getFormCashAmount(state, tabId);
    const currency = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductCurrency(state, tabId);
    const tradeWay = fromSecondaryMarket.cpOrder.form.getFormTradeWay(state, tabId)!;
    const clientType = fromSecondaryMarket.cpOrder.form.getFormClientType(state, tabId);
    const client = fromSecondaryMarket.cpOrder.referential.client.getAstroClient(state, tabId);
    const hasSelectedClient = fromSecondaryMarket.cpOrder.form.hasSelectedClient(state, tabId);
    const clientEliotCode = fromSecondaryMarket.cpOrder.form.getFormClientEliotCode(state, tabId);
    const needCustodian = clientType === 'Other';
    const selectedCustodian = fromSecondaryMarket.cpOrder.form.getFormCustodian(state, tabId);
    const disabledTradeWays = getDisabledTradeways(state, tabId);
    const tradingPriceAdjusted = fromSecondaryMarket.cpOrder.form.getFormTradingPrice(state, tabId);
    const extraAsk = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductExtraAsk(state, tabId);
    const extraBid = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductExtraBid(state, tabId);
    const defaultUpfront = getRelevantPriceDependingOnTradeWay(tradeWay, { bid: extraBid, ask: extraAsk });
    const upfront = fromSecondaryMarket.cpOrder.form.getFormUpfront(state, tabId) ?? defaultUpfront;
    const bcsProductSalesMargin = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductSalesMargin(
      state,
      tabId,
    );
    const salesMarginAdjusted = fromSecondaryMarket.cpOrder.form.getFormSalesMargin(state, tabId);
    const salesMarginPlusUpfront = getSalesMarginPlusUpfront(bcsProductSalesMargin, upfront);
    const cutOffTime = fromSecondaryMarket.cpOrder.referential.product.getBookingParamsCutoffLimitTime(state, tabId);
    const cutOffZoneId = fromSecondaryMarket.cpOrder.referential.product.getBookingParamsCutoffZoneId(state, tabId);
    const nextCutOff = fromSecondaryMarket.cpOrder.referential.product.getBookingParamsCutoffNextCutOff(state, tabId);
    const availableValuationDates =
      fromSecondaryMarket.cpOrder.referential.product.getBookingParamsAvailableValuationDates(state, tabId);
    const valuationDate = fromSecondaryMarket.cpOrder.form.getFormValuationDate(state, tabId);
    const settlementDate = fromSecondaryMarket.cpOrder.form.getFormSettlementDate(state, tabId);
    const firstSettlementDate = fromSecondaryMarket.cpOrder.form.getFormFirstSettlementDate(state, tabId);
    const tradeDateType = fromSecondaryMarket.cpOrder.form.getTradeDateType(state, tabId)!;
    const cobDate = getCobDate(valuationDate);
    const defaultSettlementType = fromSecondaryMarket.cpOrder.referential.client.getClientDefaultSettlementType(
      state,
      tabId,
      tradeWay,
    );
    const selectedSettlementType = fromSecondaryMarket.cpOrder.form.getFormSettlementType(state, tabId);
    const availableSettlementTypes = fromSecondaryMarket.cpOrder.referential.client.getClientSettlementTypes(
      state,
      tabId,
    );
    const isLoadingSettlement = fromSecondaryMarket.cpOrder.referential.client.isClientLoading(state, tabId);
    const settlementType = toSettlementTypeKey(selectedSettlementType ?? defaultSettlementType);
    const clientLoadingStatus = fromSecondaryMarket.cpOrder.referential.client.getClientLoadingStatus(state, tabId);
    const settlementHolidays = fromSecondaryMarket.cpOrder.referential.product.getBookingParamsSettlementHolidays(
      state,
      tabId,
    );
    const productOtcClients = fromSecondaryMarket.cpOrder.referential.product.getOtcDealPositions(state, tabId);
    const selectedSalesDesk = fromSecondaryMarket.cpOrder.form.getFormSalesDesk(state, tabId);
    const salesId = fromSecondaryMarket.cpOrder.form.getFormSalesId(state, tabId);
    const availableSales = getAvailableSales(state, tabId);
    const availableDesks = getDesks(state, tabId);
    const isFrozen = getTabIsFrozen(state, tabId) ?? false;
    const amountUnit = getAmountUnit(priceType, currency!);
    const tradeSizeInputMode = fromSecondaryMarket.cpOrder.formInput.getInputFormTradeSizeInputMode(state, tabId);
    return {
      isOtcProduct,
      isinCode,
      fileCode,
      ric,
      denomination,
      currency,
      maturityDate,
      quotationType,
      priceType,
      clientType: clientType!,
      productClients,
      isClientVisible: clientType === 'Other',
      traderGroup,
      portfolio,
      isSalesVisible: hasSelectedClient,
      traderMessage,
      saleMessage,
      selectedCustodian,
      cashAmount,
      quantity,
      tradeWay,
      needCustodian,
      disabledTradeWays,
      tradingPriceAdjusted,
      salesMarginAdjusted,
      salesMarginPlusUpfront,
      upfront,
      amountUnit,
      cutOffTime,
      cutOffZoneId,
      nextCutOff,
      settlementDate,
      firstSettlementDate,
      settlementHolidays,
      availableValuationDates,
      valuationDate,
      tradeDateType,
      cobDate,
      settlementType,
      availableSettlementTypes,
      isLoadingSettlement,
      clientLoadingStatus,
      client: hasSelectedClient ? client : undefined,
      clientEliotCode,
      productOtcClients,
      availableDesks,
      availableSales,
      salesId,
      selectedSalesDesk,
      isFrozen,
      tradeSizeInputMode,
      minQuantity: getCpOrderReferentialMinQuantity(state, tabId),
      maxQuantity: getMaxQuantity(state, tabId),
      remaining: getCpOrderReferentialRemaining(state, tabId),
      lot: getCpOrderReferentialLotSize(state, tabId),
    };
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch, { tabId }: OwnProps): DispatchProps => {
  return {
    onClientTypeChanged: value => {
      dispatch(secondaryMarketCpOrderClientTypeSelectedThunk(tabId, 'secondaryMarketQuotes', value));
    },
    onCashAmountChanged: value => {
      dispatch(secondaryMarketCpOrderCashAmountChangedThunk(tabId, value));
    },
    onCashAmountReset: () => {
      dispatch(secondaryMarketCpOrderCashAmountResetThunk(tabId));
    },
    onQuantityChanged: value => {
      dispatch(secondaryMarketCpOrderQuantityChangedThunk(tabId, value));
    },
    onQuantityReset: () => {
      dispatch(secondaryMarketCpOrderQuantityResetThunk(tabId));
    },
    onTradeWayChanged: value => {
      dispatch(secondaryMarketCpOrderTradeWayChangedThunk(tabId, value));
    },
    onCustodianChanged: value => {
      dispatch(secondaryMarketCpOrderCustodianSelectedThunk(tabId, 'secondaryMarketQuotes', value));
    },
    onTradingPriceChanged: value => {
      dispatch(secondaryMarketCpOrderTradingPriceChangedThunk(tabId, value));
    },
    onSalesMarginChanged: value => {
      dispatch(secondaryMarketCpOrderSalesMarginChangedThunk(tabId, value));
    },
    onSettlementDateChanged(value) {
      dispatch(secondaryMarketCpOrderSettlementDateChangedThunk(tabId, value));
    },
    onValuationDateChanged(value) {
      dispatch(secondaryMarketCpOrderFormValuationDateChangedThunk(tabId, 'secondaryMarketQuotes', value));
    },
    onTradeDateTypeChanged(value) {
      dispatch(secondaryMarketCpOrderFormTradeDateTypeChangedThunk(tabId, 'secondaryMarketQuotes', value));
    },
    onSettlementTypeChanged(value) {
      dispatch(secondaryMarketCpOrderSettlementTypeChangedThunk(tabId, value));
    },
    onClientChanged: client => {
      dispatch(secondaryMarketCpOrderClientSelectedThunk(tabId, 'secondaryMarketQuotes', client));
    },
    onClientClear: () => {
      dispatch(secondaryMarketCpOrderClientSelectedThunk(tabId, 'secondaryMarketQuotes', undefined));
    },
    onRetryLoadingClient: () => {
      dispatch(secondaryMarketCpFormLinkedToClientRequested(tabId));
    },
    onSalesChanged: salesId => {
      dispatch(secondaryMarketCpOrderSelectSalesIdThunk(tabId, salesId));
    },
    onSalesDeskChanged: salesDesk => {
      dispatch(secondaryMarketCpOrderSelectSalesDeskThunk(tabId, salesDesk));
    },
    onTradeSizeInputModeChanged() {
      dispatch(secondaryMarketCpOrderTradeSizeInputModeChangedThunk(tabId));
    },
  };
};

export const connectCpOrderFormContent = connect(mapStateToProps, mapDispatchToProps);
